import React, { useState } from 'react';
import { Send, Palette, ChevronRight } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { StatCard } from '../StatCard';
import { Button } from '../SubscriptionAppButton';
import { Card, SegmentedControl, StatusPill } from './walletUi';
import { walletKpis, walletTrend, walletNotifications, walletTiers } from '../../data/mockDigitalWallet';
export function WalletOverviewTab({
  onGoTo
}: {
  onGoTo: (tab: string) => void;
}) {
  const [range, setRange] = useState<'7' | '30' | '90' | 'all'>('30');
  const [reference, setReference] = useState<'issue' | 'download'>('issue');
  const adoption = Math.round(walletKpis.inWallet / walletKpis.loyaltyMembers * 100);
  const data = range === '7' ? walletTrend.slice(-7) : walletTrend;
  const recent = walletNotifications.filter(n => n.status === 'Sent').slice(0, 3);
  return <div className="space-y-6" data-id="element-2760" data-mp="3nq0lwvsyqw98">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-id="element-2761" data-mp="3nopkv3qwk2lt">
        <StatCard label="Passes issued" value={walletKpis.issued.toLocaleString()} secondaryValue="One per loyalty member" data-id="element-2762" data-mp="6oum9gbqnkuk" />
        <StatCard label="In a wallet" value={walletKpis.inWallet.toLocaleString()} secondaryValue={`${adoption}% of loyalty members`} trend={{
        value: '+6% vs last month',
        isPositive: true
      }} data-id="element-2763" data-mp="bp9z86s3yqc0" />
        <StatCard label="Apple / Google" value={`${walletKpis.apple} / ${walletKpis.google}`} secondaryValue={`${walletKpis.both} members have both`} data-id="element-2764" data-mp="3k3imr0frwqxb" />
        <StatCard label="Removed" value={walletKpis.removed} secondaryValue="Last 30 days" data-id="element-2765" data-mp="3p3wx4p4ukcyi" />
      </div>

      <Card title="Passes over time" description={reference === 'issue' ? 'Each day shows the passes issued that day and how many of those have since been added to a wallet.' : 'Each day shows the passes added to a wallet that day, whenever they were issued.'} action={<div className="flex items-center gap-2" data-id="element-2767" data-mp="1dd1alqafyvdk">
            <SegmentedControl value={reference} onChange={setReference} options={[{
        value: 'issue',
        label: 'By issue date'
      }, {
        value: 'download',
        label: 'By added date'
      }]} data-id="element-2768" data-mp="1daottggf6r23" />
            <SegmentedControl value={range} onChange={setRange} options={[{
        value: '7',
        label: '7d'
      }, {
        value: '30',
        label: '30d'
      }, {
        value: '90',
        label: '90d'
      }, {
        value: 'all',
        label: 'All'
      }]} data-id="element-2769" data-mp="11xg7kh6yi5pm" />
          </div>} data-id="element-2766" data-mp="3a6mf0wxp88tt">
        <div className="h-[280px]" data-id="element-2770" data-mp="1pnagwos2hc54">
          <ResponsiveContainer width="100%" height="100%" data-id="element-2771" data-mp="1po253ejc47ld">
            <BarChart data={data} margin={{
            top: 10,
            right: 10,
            left: -10,
            bottom: 0
          }} data-id="element-2772" data-mp="1kw27hsqqlqry">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-2773" data-mp="1gtxcoqojyo3q" />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
              fill: '#94A3B8',
              fontSize: 11
            }} interval={range === '7' ? 0 : 4} data-id="element-2774" data-mp="2ddmvgihti74o" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#94A3B8',
              fontSize: 11
            }} data-id="element-2775" data-mp="3r9ek39v461lj" />
              <Tooltip contentStyle={{
              borderRadius: 8,
              border: '1px solid #E2E8F0'
            }} data-id="element-2776" data-mp="2lk29c248il" />
              <Legend iconType="circle" verticalAlign="top" height={30} data-id="element-2777" data-mp="9304z7l3zllk" />
              {reference === 'issue' && <Bar dataKey="issued" name="Issued" fill="#CBD5E1" radius={[3, 3, 0, 0]} data-id="element-2778" data-mp="e24cltsdxb9s" />}
              <Bar dataKey="apple" name="Added to Apple Wallet" stackId="w" fill="#0EA5E9" data-id="element-2779" data-mp="e0a279eqp1jy" />
              <Bar dataKey="google" name="Added to Google Wallet" stackId="w" fill="#6F42C1" radius={[3, 3, 0, 0]} data-id="element-2780" data-mp="iz39gk3fnwbo" />
              <Bar dataKey="removed" name="Removed" fill="#F87171" radius={[3, 3, 0, 0]} data-id="element-2781" data-mp="rthtezywrv14" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2782" data-mp="wuf9n70cyezz">
        <Card title="Recent notifications" action={<Button variant="ghost" size="sm" iconRight={<ChevronRight className="w-4 h-4" data-id="element-2785" data-mp="o46q2lc54xpo" />} onClick={() => onGoTo('notifications')} data-id="element-2784" data-mp="11pkxwch8g5r8">
              View all
            </Button>} data-id="element-2783" data-mp="11sqndstrx9nh">
          <div className="divide-y divide-[#F1F5F9]" data-id="element-2786" data-mp="j5c79je6b95k">
            {recent.map(n => <div key={n.id} className="py-3 flex items-start justify-between gap-4" data-id="element-2787" data-mp="xv1gm1wf6jgx">
                <div className="min-w-0" data-id="element-2788" data-mp="sv5bfzbzklzs">
                  <div className="text-sm font-medium text-[#0F172A] truncate" data-id="element-2789" data-mp="swgnemfl501z">{n.title}</div>
                  <div className="text-xs text-[#64748B]" data-id="element-2790" data-mp="56m9s2nfz7go">{n.when} · {n.audience}</div>
                </div>
                <div className="text-right flex-shrink-0" data-id="element-2791" data-mp="8u8ii0wq8zt">
                  <StatusPill status={n.status} data-id="element-2792" data-mp="f4jxmp1e328r" />
                  <div className="text-xs text-[#64748B] mt-1" data-id="element-2793" data-mp="f27azykos3pd">{n.passes.toLocaleString()} passes</div>
                </div>
              </div>)}
          </div>
        </Card>

        <Card title="Passes by tier" description="Members in each tier with the pass in a wallet." data-id="element-2794" data-mp="1qhvugfyqeklf">
          <div className="space-y-3" data-id="element-2795" data-mp="1ljubdv1b2l9y">
            {walletTiers.map(t => {
            const inWallet = Math.round(t.members * 0.69);
            const pct = Math.round(inWallet / t.members * 100);
            return <div key={t.id} data-id="element-2796" data-mp="1y49gzw2okpda">
                  <div className="flex items-center justify-between text-sm mb-1" data-id="element-2797" data-mp="1k36u5h3v3b1b">
                    <span className="flex items-center gap-2 text-[#0F172A]" data-id="element-2798" data-mp="1k5jjwgfv2pox">
                      <span className="w-3 h-3 rounded-sm" style={{
                    background: t.background
                  }} data-id="element-2799" data-mp="1o524fb3rsyr8" />
                      {t.name}
                    </span>
                    <span className="text-[#64748B] tabular-nums" data-id="element-2800" data-mp="2gsxzychlskmf">
                      {inWallet} of {t.members} · {pct}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden" data-id="element-2801" data-mp="272uz5q48q2dj">
                    <div className="h-full rounded-full bg-[#0EA5E9]" style={{
                  width: `${pct}%`
                }} data-id="element-2802" data-mp="2usp65r1vowpv" />
                  </div>
                </div>;
          })}
          </div>
          <div className="flex gap-2 mt-6" data-id="element-2803" data-mp="39g7fx3uashj5">
            <Button variant="primary" size="sm" iconLeft={<Send className="w-4 h-4" data-id="element-2805" data-mp="35dsxulygdlsx" />} onClick={() => onGoTo('notifications:new')} data-id="element-2804" data-mp="39f4nusl8o91n">
              Send a notification
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<Palette className="w-4 h-4" data-id="element-2807" data-mp="2vger5e2c0bdt" />} onClick={() => onGoTo('design')} data-id="element-2806" data-mp="30dof0pa2oezj">
              Edit pass design
            </Button>
          </div>
        </Card>
      </div>
    </div>;
}